import { useCallback, useRef } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { WEBRTC_CONNECTION_QUERY_KEY } from 'constants/QueryKeys.constants';

import api from 'api';
import type {
  CreateWebRTCConnectionRequest,
  GetWebRTCConnectionResponse,
  WebRTCConnectionResponse,
} from 'api/types';
import { useIsPlgProd } from 'hooks/useIsPlgProd';

const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
];

export type UseWebRTCOptions = {
  connectionId?: string;
  iceServers?: RTCIceServer[];
  onRemoteStream?: (stream: MediaStream) => void;
  onIceCandidate?: (candidate: RTCIceCandidate) => void;
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
};

export type UseWebRTCReturn = {
  createOffer: (localStream: MediaStream) => Promise<RTCSessionDescriptionInit>;
  setRemoteAnswer: (sdp: string) => Promise<void>;
  addIceCandidate: (candidate: RTCIceCandidateInit) => Promise<void>;
  replaceTrack: (newTrack: MediaStreamTrack) => Promise<void>;
  close: () => void;
  connectionData: GetWebRTCConnectionResponse | undefined;
  isConnectionLoading: boolean;
  connectionError: unknown;
  createWebRTCConnectionAsync: (
    body: CreateWebRTCConnectionRequest,
  ) => Promise<WebRTCConnectionResponse>;
  isCreatingConnection: boolean;
  createConnectionError: unknown;
};

const useWebRTC = (options?: UseWebRTCOptions): UseWebRTCReturn => {
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const remoteDescriptionSetRef = useRef(false);
  const pendingCandidatesRef = useRef<RTCIceCandidateInit[]>([]);
  const optionsRef = useRef(options);
  const isPlgProd = useIsPlgProd();

  optionsRef.current = options;

  const {
    mutateAsync: createWebRTCConnectionAsync,
    isLoading: isCreatingConnection,
    error: createConnectionError,
  } = useMutation({
    mutationFn: (body: CreateWebRTCConnectionRequest) =>
      api.createWebRTCConnection(body),
  });

  const {
    data: connectionData,
    isLoading: isConnectionLoading,
    error: connectionError,
  } = useQuery({
    queryKey: [WEBRTC_CONNECTION_QUERY_KEY, options?.connectionId],
    queryFn: () => api.getWebRTCConnection(options?.connectionId!),
    enabled: !!options?.connectionId && isPlgProd,
  });

  const createPeerConnection = useCallback(() => {
    // Close existing connection to prevent leaks
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    remoteDescriptionSetRef.current = false;
    pendingCandidatesRef.current = [];

    const iceServers = optionsRef.current?.iceServers ?? DEFAULT_ICE_SERVERS;
    const pc = new RTCPeerConnection({ iceServers });

    pc.onicecandidate = event => {
      if (event.candidate) {
        optionsRef.current?.onIceCandidate?.(event.candidate);
      }
    };

    pc.ontrack = event => {
      const [remoteStream] = event.streams;
      if (remoteStream) {
        optionsRef.current?.onRemoteStream?.(remoteStream);
      }
    };

    pc.onconnectionstatechange = () => {
      optionsRef.current?.onConnectionStateChange?.(pc.connectionState);
    };

    pcRef.current = pc;
    return pc;
  }, []);

  const createOffer = useCallback(
    async (localStream: MediaStream): Promise<RTCSessionDescriptionInit> => {
      const pc = createPeerConnection();

      const audioTracks = localStream.getAudioTracks();

      for (const audioTrack of audioTracks) {
        pc.addTrack(audioTrack, localStream);
      }

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      return offer;
    },
    [createPeerConnection],
  );

  const setRemoteAnswer = useCallback(async (sdp: string) => {
    if (!pcRef.current) {
      return;
    }
    const answer = new RTCSessionDescription({ type: 'answer', sdp });
    await pcRef.current.setRemoteDescription(answer);
    remoteDescriptionSetRef.current = true;

    // Flush any queued remote ICE candidates
    const pending = pendingCandidatesRef.current;
    pendingCandidatesRef.current = [];
    for (const c of pending) {
      await pcRef.current.addIceCandidate(new RTCIceCandidate(c));
    }
  }, []);

  const addIceCandidate = useCallback(
    async (candidate: RTCIceCandidateInit) => {
      if (!pcRef.current) {
        return;
      }
      // Queue candidates until remote description is set (Firefox compat)
      if (!remoteDescriptionSetRef.current) {
        pendingCandidatesRef.current.push(candidate);
        return;
      }
      await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
    },
    [],
  );

  const replaceTrack = useCallback(async (newTrack: MediaStreamTrack) => {
    if (!pcRef.current) {
      return;
    }
    const sender = pcRef.current
      .getSenders()
      .find(s => s.track?.kind === 'audio');
    if (sender) {
      await sender.replaceTrack(newTrack);
    }
  }, []);

  const close = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    remoteDescriptionSetRef.current = false;
    pendingCandidatesRef.current = [];
  }, []);

  return {
    createWebRTCConnectionAsync,
    isCreatingConnection,
    createConnectionError,
    connectionData,
    isConnectionLoading,
    connectionError,
    createOffer,
    setRemoteAnswer,
    addIceCandidate,
    replaceTrack,
    close,
  };
};

export default useWebRTC;
